/**
 * Management > Campaigns.
 *
 * A campaign is a hierarchical CampaignStep tree tracked two independent ways — per-SE enablement
 * and customer-facing delivery — composed of seven generic-CRUD tables. This page has two views,
 * switched by a segmented control:
 *
 *   - **Builder** (primary): a real authoring surface that creates and manages the Campaign record
 *     and its CampaignStep tree — a step tree, per-step detail forms, and a grounded AI Guide, styled
 *     in a scoped design system (see `builder/`). This is where campaigns are built.
 *   - **Analytics** (secondary): the visualized Program Analytics dashboard.
 *
 * The two views ride the existing `:section` route param (no `App.tsx` change): an empty section (or
 * `builder`) is the Builder; `analytics` is the dashboard. The Builder tracks its open campaign and
 * selected step in `?c=`/`?step=` query params. Write controls degrade to read-only when the viewer
 * lacks `api.campaign.*` grants.
 */
import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import NavHistoryTracker from '../../../navigation/NavHistoryTracker';
import { useAdminPermissions } from '../Hooks/useAdminPermissions';
import { CAMPAIGN_ADMIN_MODEL_KEYS, MODELS, ModelConfig } from '../RbacConfig';
import { ADMIN_URL_CAMPAIGNS } from '../PageStructure/admin-values';
import CampaignBuilder from './builder/CampaignBuilder';
import CampaignReferences from './builder/CampaignReferences';
import ProgramAnalytics from './analytics/ProgramAnalytics';
import { TokenFieldProvider } from './builder/TokenFieldContext';

// Visualized Program Analytics section. On by default; set REACT_APP_CAMPAIGN_ANALYTICS=false to
// deploy it dark.
const ANALYTICS_ENABLED = process.env.REACT_APP_CAMPAIGN_ANALYTICS !== 'false';

const AdminCampaigns: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { section } = useParams();
  const { isVisibleTab, isAdminFull, canCreateModel, canUpdateModel } = useAdminPermissions();

  const campaignModels: Record<string, ModelConfig> = {};
  CAMPAIGN_ADMIN_MODEL_KEYS.forEach((key) => {
    const model = MODELS[key];
    if (model) campaignModels[key] = model;
  });

  const modelKeys = Object.keys(campaignModels).filter((key) => isVisibleTab(campaignModels[key]));

  // The Builder's write controls degrade to read-only unless the viewer can create/update campaigns.
  const canWrite =
    isAdminFull || canUpdateModel(MODELS.campaigns) || canCreateModel(MODELS.campaigns);

  // Section drives the view: `analytics` is the dashboard; `references` is the reusable-attribute
  // catalogue; anything else (empty, `builder`) is the Builder.
  const mode: 'builder' | 'analytics' | 'references' =
    section === 'analytics' && ANALYTICS_ENABLED
      ? 'analytics'
      : section === 'references'
      ? 'references'
      : 'builder';

  const canReadAnyCampaignData = isVisibleTab(MODELS.campaigns) || modelKeys.length > 0;

  // The open campaign (`?c=`) drives which campaign's variables the ambient `{{`-token fields offer;
  // parsed here so the whole builder / reference tree below sits under one TokenFieldProvider. Absent
  // (no campaign open) → undefined, and the token fields fall back to universal/static variables.
  const openCampaignId = React.useMemo(() => {
    const c = new URLSearchParams(location.search).get('c');
    return c && Number.isFinite(Number(c)) ? Number(c) : undefined;
  }, [location.search]);

  // Builder preserves the open campaign (`?c=`). Reference Objects opens the GLOBAL library (no `?c=`):
  // the shared catalogue is managed unscoped there, while a campaign links what it needs from the
  // Builder. Analytics has its own scoping so it starts fresh.
  const goBuilder = () => navigate(`${ADMIN_URL_CAMPAIGNS}${location.search}`);
  const goReferences = () => navigate(`${ADMIN_URL_CAMPAIGNS}/references`);
  const goAnalytics = () => navigate(`${ADMIN_URL_CAMPAIGNS}/analytics`);

  if (!canReadAnyCampaignData) {
    return (
      <>
        <h1 className="mt-0 pb-6 border-b border-gray-15 text-gray-60 font-semibold leading-5">
          Campaigns
        </h1>
        <p className="mt-4 text-gray-60">You do not have permission to manage campaigns.</p>
      </>
    );
  }

  const segBase =
    'px-4 py-2 -mb-px border-b-2 bg-transparent cursor-pointer text-sm transition-colors';
  const segActive = `${segBase} border-gray-90 text-gray-90 font-semibold`;
  const segIdle = `${segBase} border-transparent text-gray-60 hover:text-gray-90`;

  return (
    <>
      <NavHistoryTracker title={mode} />
      <h1 className="mt-0 pb-6 border-b border-gray-15 text-gray-60 font-semibold leading-5">
        Campaigns
      </h1>

      <div className="mt-4 mb-6 flex gap-2 border-b border-gray-15">
        <button type="button" className={mode === 'builder' ? segActive : segIdle} onClick={goBuilder}>
          Builder
        </button>
        <button
          type="button"
          className={mode === 'references' ? segActive : segIdle}
          onClick={goReferences}
        >
          Reference Objects
        </button>
        {ANALYTICS_ENABLED && (
          <button
            type="button"
            className={mode === 'analytics' ? segActive : segIdle}
            onClick={goAnalytics}
          >
            Analytics
          </button>
        )}
      </div>

      <TokenFieldProvider campaignId={openCampaignId}>
        {mode === 'analytics' ? (
          <ProgramAnalytics />
        ) : mode === 'references' ? (
          <CampaignReferences canWrite={canWrite} />
        ) : (
          <CampaignBuilder canWrite={canWrite} />
        )}
      </TokenFieldProvider>
    </>
  );
};

export default AdminCampaigns;
